/**
 * useGeolocateControl — AK-388: кнопка «моё местоположение» на карте, ТОЛЬКО на
 * мобиле (iOS + Android). На десктопе контрол НЕ добавляется вовсе.
 *
 * Синяя точка «где я» + слежение (`trackUserLocation`) — нативный MapLibre
 * `GeolocateControl`. На десктопе (мышь, без GPS) кнопка бесполезна и мусорит
 * UI, поэтому гейтим по `detectPlatform()` (тот же детектор, что у навигатор-
 * ссылок AK-372/380 — UA + maxTouchPoints, ловит iPadOS-маскировку под Mac).
 *
 * TEARDOWN-SAFE (грабля тройного дропа guard'ов V2 — #373/#374/#394):
 *   при unmount карта могла уже пройти `map.remove()` (style === null,
 *   `_removed === true`) — тогда `removeControl` дёрнет style-методы на мёртвой
 *   карте → TypeError → белый экран. Снимаем контрол ТОЛЬКО пока `isMapAlive`,
 *   и оборачиваем в try/catch на случай, что стиль сносится параллельно.
 *
 * FAIL-SOFT:
 *   отказ в доступе / ошибка геолокации → событие `error` контрола. Не роняем
 *   карту: гасим спиннер (нативный контрол сам возвращается в неактивное
 *   состояние) и отдаём внятное сообщение через `onError` (тост в v2.tsx).
 *   Браузерный prompt доступа показывается системой — это ожидаемо, не обходим.
 */
import { useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { detectPlatform } from '@/lib/geo/navigateLinks';
import { isMapAlive } from '@/lib/map/isMapAlive';

export interface GeolocateControlHookOptions {
  /**
   * Вызывается при ошибке/отказе геолокации с готовым сообщением для тоста.
   * Должен быть стабильной ссылкой (state-setter / useCallback), иначе эффект
   * будет пересоздавать контрол на каждый рендер.
   */
  onError?: (message: string) => void;
}

/**
 * Собрать сообщение об ошибке геолокации по коду `GeolocationPositionError`.
 *   1 = PERMISSION_DENIED · 2 = POSITION_UNAVAILABLE · 3 = TIMEOUT.
 */
function geolocateErrorMessage(code: number | undefined): string {
  if (code === 1) {
    return 'Доступ к геолокации запрещён. Разрешите его в настройках браузера.';
  }
  return 'Не удалось определить местоположение.';
}

/**
 * Добавляет нативный `GeolocateControl` на карту при `ready`, но только на
 * мобильных платформах. Снимает его teardown-safe при unmount / смене карты.
 *
 * @param map   инстанс карты (null до создания — эффект ждёт).
 * @param ready карта готова принимать контролы (обычно `styleReady`).
 */
export function useGeolocateControl(
  map: maplibregl.Map | null,
  ready: boolean,
  options: GeolocateControlHookOptions = {},
): void {
  const { onError } = options;
  useEffect(() => {
    if (!map || !ready) return;
    // Мобила only. Десктоп → контрол не добавляем вовсе (п.1 AK-388).
    if (detectPlatform() === 'desktop') return;

    // NB: maplibre-gl v5 GeolocateControl НЕ поддерживает `showUserHeading`
    // (был в mapbox-gl, в maplibre v5 отсутствует в GeolocateControlOptions).
    // Синяя точка «где я» — `showUserLocation` (по умолчанию true), слежение —
    // `trackUserLocation`. Точность — enableHighAccuracy (GPS вместо сети).
    const control = new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserLocation: true,
    });

    // Fail-soft: ошибка/отказ геолокации не роняет карту. Событие `error`
    // несёт GeolocationPositionError (.code/.message). Типизируем свободно —
    // payload у maplibre обёрнут в Event, поле code читаем аккуратно.
    const handleError = (e: unknown) => {
      const code = (e as { code?: number } | undefined)?.code;
      onError?.(geolocateErrorMessage(code));
    };
    control.on('error', handleError);

    // bottom-right: не конфликтует с RightRail (top-right). Контрол
    // позиционируется внутри контейнера карты (который заполняет <main>),
    // поэтому при появлении 2FA-banner он отжимается автоматически — без
    // хардкода высоты (CLAUDE.md «Адаптивная вёрстка»).
    map.addControl(control, 'bottom-right');

    return () => {
      control.off('error', handleError);
      // Teardown-safe: не трогаем контрол на уже снесённой карте.
      if (isMapAlive(map)) {
        try {
          map.removeControl(control);
        } catch {
          // Стиль сносится параллельно — контрол уже отсоединён, игнорируем.
        }
      }
    };
  }, [map, ready, onError]);
}
